import { Body4 } from '@/components/typography';
import { H3, HTable } from '@/components/typography/heading/headings.component';
import { Table, TableBody, TableCellSmall, TableHeader, TableHeadSmall, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ProjectReportingType } from '@/modules/Customers/Customers.api';
import { getFarmIdSuffix } from '@/modules/ProjectDetails/ProjectDetailsMap';
import { type ProjectMapData } from '@/modules/Projects/Projects.schema';
import { useNavigate } from 'react-router-dom';

const toSentenceCase = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

type ProjectMapProps = {
  data: ProjectMapData;
  customerId?: string;
  projectId?: string;
  className?: string;
  reportingType?: ProjectReportingType;
};

export const ProjectMapTable = ({ customerId, projectId, data, className, reportingType }: ProjectMapProps) => {
  const navigate = useNavigate();
  const href = customerId && projectId ? `customers/${customerId}/projects/${projectId}/farms` : '';
  // fixed table height when the table becomes scrollable after having more than 8 fields/rows
  const tableHeightStyle = data && data.valid_farms?.length > 8 ? 'h-[300px]' : 'h-auto';
  const isClickable = reportingType !== 'project_only';

  return (
    <div className={cn('overflow-y-auto border border-neutral-200 rounded-lg', className, tableHeightStyle)}>
      <Table className="min-w-full">
        <TableHeader className="sticky-table-header">
          <TableRow>
            <TableHeadSmall>Farm Name</TableHeadSmall>
            <TableHeadSmall>Farm ID</TableHeadSmall>
            <TableHeadSmall>Carbon Stock</TableHeadSmall>
          </TableRow>
        </TableHeader>
        <TableBody className="border-t-2">
          {data && data.valid_farms?.length ? (
            [...data.valid_farms]
              .sort((a, b) => (a.farm_id || '').localeCompare(b.farm_id || ''))
              .map((farm, index) => {
                const isUnmatched = !farm.farm_id;
                const farmName = getFarmIdSuffix(farm.farm_id || '');
                const rowClickable = isClickable && !isUnmatched && farm.farm_id;
                return (
                  <TableRow
                    key={index}
                    onClick={() => {
                      if (rowClickable) {
                        navigate(`/${href}/${farm.farm_id}`);
                      }
                    }}
                    className={cn(rowClickable ? 'cursor-pointer hover:bg-neutral-100 transition-colors' : '')}
                  >
                    <TableCellSmall>{farmName}</TableCellSmall>
                    <TableCellSmall>{farm.farm_id}</TableCellSmall>
                    <TableCellSmall>{farm.cstock_comment ? toSentenceCase(farm.cstock_comment) : 'Unknown'}</TableCellSmall>
                  </TableRow>
                );
              })
          ) : (
            <TableRow>
              <TableCellSmall colSpan={3} className="text-center">
                No field data available
              </TableCellSmall>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export const CarbonStockKeyTable = () => (
  <div className="w-full">
    <Table>
      <HTable className={cn('text-brand my-1')}>
        <H3 className="text-center">Carbon Stock</H3>
      </HTable>
      <Body4 className={cn('text-center mb-2')}>High, Medium or Low within your farm</Body4>
      <TableBody className="border-t-2">
        <TableRow className={cn('flex flex-row justify-between mx-4')}>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-green')} />
            High
          </TableCellSmall>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-yellow')} />
            Medium
          </TableCellSmall>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-red')} />
            Low
          </TableCellSmall>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-grey')} />
            Not yet sampled
          </TableCellSmall>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);
